/**
 * How chat messages are laid out. "reading" shows bot replies as borderless text in a
 * centred reading column (Claude Desktop style); "compact" keeps the chat bubbles.
 */
export type MessageLayout = "reading" | "compact";

/** Local storage key of the chosen message layout. */
export const MESSAGE_LAYOUT_STORAGE_KEY = "rakazo.messageLayout";

type LayoutStorage = Pick<Storage, "getItem" | "setItem">;

const listeners = new Set<() => void>();
let memoryPreference: MessageLayout | null = null;

function getLocalStorage(): LayoutStorage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** Only a saved "compact" brings the bubbles back; everything else is the reading layout. */
export function normalizeMessageLayout(raw: string | null | undefined): MessageLayout {
  return raw?.trim().toLowerCase() === "compact" ? "compact" : "reading";
}

/** Reads the saved layout; falls back to the reading layout when storage is missing or blocked. */
export function readMessageLayout(
  storage: Pick<Storage, "getItem"> | null = getLocalStorage(),
): MessageLayout {
  if (!storage) return "reading";
  try {
    return normalizeMessageLayout(storage.getItem(MESSAGE_LAYOUT_STORAGE_KEY));
  } catch {
    return "reading";
  }
}

/** Saves the layout; failures are ignored so the in-memory choice still applies. */
export function persistMessageLayout(
  layout: MessageLayout,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(MESSAGE_LAYOUT_STORAGE_KEY, normalizeMessageLayout(layout));
  } catch {
    // Keep the in-memory preference when local storage is unavailable or blocked.
  }
}

/** Current layout: the choice made in this session, else the saved one. */
export function getMessageLayout(): MessageLayout {
  return memoryPreference ?? readMessageLayout();
}

/** Applies and saves a layout and tells every subscriber, so the chat switches without a reload. */
export function setMessageLayout(layout: MessageLayout): MessageLayout {
  memoryPreference = normalizeMessageLayout(layout);
  persistMessageLayout(memoryPreference);
  for (const listener of listeners) listener();
  return memoryPreference;
}

/** Subscribes to layout changes (for useSyncExternalStore); returns the unsubscribe function. */
export function subscribeMessageLayout(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test helper: forget the in-memory choice so the next read comes from storage. */
export function resetMessageLayoutForTests(): void {
  memoryPreference = null;
}
